import { Capabilities } from '../../services/hooks/useCapabilities';
import moment, { Moment } from 'moment-timezone';
import { MapLayer } from './MapConfig';

export enum CapabilityMergeStrategy {
  UseFirst,
  Union,
}

export interface TimedGroup {
  key: string;
  layers: MapLayer[];
  strategy: CapabilityMergeStrategy;
  timeBounds?: {
    min?: moment.Duration;
    max?: moment.Duration;
  };
}

function useFirst(
  layers: MapLayer[],
  capabilities: Capabilities
): Moment[] | undefined {
  const layer = layers[0];
  const cap = capabilities[layer.key];
  if (!cap) return undefined;
  return cap.times;
}

function union(
  layers: MapLayer[],
  capabilities: Capabilities
): Moment[] | undefined {
  const times = {};
  layers.forEach((layer) => {
    const cap = capabilities[layer.key];
    if (!cap?.times) return;
    cap.times.forEach((time) => (times[time.toISOString()] = time));
  });
  const keys = Object.keys(times);
  keys.sort();
  return keys.length ? keys.map((key) => times[key]) : undefined;
}

function closestTime(times: Moment[]) {
  const now = moment.utc();
  if (!times?.length) return now;
  let best = 0;
  let bestDelta = Math.abs(times[0].diff(now));
  for (let i = 1; i < times.length; ++i) {
    const delta = Math.abs(times[i].diff(now));
    if (delta < bestDelta) {
      best = i;
      bestDelta = delta;
    }
  }
  return times[best].clone();
}

function clamp(
  group: TimedGroup,
  times: Moment[] | undefined,
  capabilities: Capabilities
) {
  if (!times) return times;
  if (!group.timeBounds) return times;
  const refTimes = group.layers
    .map((layer) => capabilities[layer.key]?.refTime)
    .filter((val) => !!val);
  const refTime = refTimes.length ? moment(refTimes[0]) : closestTime(times);
  if (group.timeBounds.min) {
    const minTime = refTime.add(group.timeBounds.min);
    times = times.filter((time) => time.isSameOrAfter(minTime));
  }
  if (group.timeBounds.max) {
    const maxTime = refTime.add(group.timeBounds.min);
    times = times.filter((time) => time.isBefore(maxTime));
  }
  return times;
}

const strategyToMethod = {
  [CapabilityMergeStrategy.UseFirst]: useFirst,
  [CapabilityMergeStrategy.Union]: union,
};

export const calculateGroupTimes = (
  group: TimedGroup,
  capabilities: Capabilities
): Moment[] | undefined => {
  const times = strategyToMethod[group.strategy](group.layers, capabilities);
  return clamp(group, times, capabilities);
};
